import type React from 'react';
import { useCallback, useEffect, useId, useState } from 'react';

import classNames from 'classnames';

import { insertEmojiCompose } from '@/mastodon/actions/compose';
import type { OnEmojiPick } from '@/mastodon/components/emoji/picker';
import { normalizeKey } from '@/mastodon/components/hotkeys/utils';
import {
  closeComposer,
  getComposerTextarea,
  selectComposerIsChanged,
  submitComposer,
} from '@/mastodon/reducers/slices/composer';
import { useAppDispatch, useAppSelector } from '@/mastodon/store';

import { ComposeAttachments } from './components/attachments';
import { ComposeCanvas } from './components/canvas';
import canvasClasses from './components/canvas.module.scss';
import { ComposeFooter } from './components/footer';
import { ComposeFormHeader } from './components/header';
import { ComposeHints } from './components/hints';
import { LanguageButton } from './components/language';
import { ComposeReply } from './components/reply';
import { ComposeSchedule } from './components/schedule';
import { selectComposeType } from './components/selectors';
import { ComposeSensitiveField } from './components/sensitive';
import { ComposeSettingsMenu } from './components/settings';
import { ComposeTextarea } from './components/textarea';
import { ComposeVisibility } from './components/visibility';
import classes from './styles.module.scss';

interface ComposeFormProps {
  autoFocus?: boolean;
  headless?: boolean;
  className?: string;
  noMinimize?: boolean;
  redirectOnSuccess?: boolean;
}

export const ComposeForm: React.FC<
  ComposeFormProps & React.ComponentPropsWithRef<'form'>
> = ({
  autoFocus,
  headless,
  className,
  noMinimize,
  redirectOnSuccess,
  ...props
}) => {
  const type = useAppSelector(selectComposeType);

  const { onEmojiPick, onSubmit } = useComposeHandlers({
    redirectOnSuccess,
    headless,
  });

  const titleId = useId();
  const hintsId = useId();

  // Creatodon: 手書き Canvas の表示切り替え
  const [isDrawing, setIsDrawing] = useState(false);
  const [isCanvasExpanded, setIsCanvasExpanded] = useState(false);
  const handleOpenCanvas = useCallback(() => {
    setIsDrawing(true);
  }, []);
  const handleCloseCanvas = useCallback(() => {
    setIsDrawing(false);
    setIsCanvasExpanded(false);
  }, []);
  const handleToggleCanvasExpand = useCallback(() => {
    setIsCanvasExpanded((expanded) => !expanded);
  }, []);

  return (
    <form
      {...props}
      role='dialog'
      onSubmit={onSubmit}
      aria-labelledby={titleId}
      className={classNames(
        className,
        classes.root,
        headless && classes.headless,
        isDrawing && isCanvasExpanded && canvasClasses.expandedForm,
      )}
    >
      {(type === 'message' || type === 'replyPrivate') && (
        <div className={classes.background} />
      )}

      {!headless && <ComposeFormHeader id={titleId} noMinimize={noMinimize} />}

      <div className={classes.toolbar}>
        <ComposeVisibility className={classes.flexGrowWrap} />

        <LanguageButton />

        <ComposeSettingsMenu />
      </div>

      {isDrawing ? (
        <ComposeCanvas
          onClose={handleCloseCanvas}
          expanded={isCanvasExpanded}
          onToggleExpand={handleToggleCanvasExpand}
        />
      ) : (
        <>
          <ComposeTextarea
            autoFocus={autoFocus}
            aria-describedby={hintsId}
            beforeTextArea={
              <>
                <ComposeReply />

                <ComposeSensitiveField />
              </>
            }
          >
            <ComposeAttachments className={classes.attachments} />

            <ComposeSchedule className={classes.attachments} />
          </ComposeTextarea>

          <ComposeHints id={hintsId} />

          <ComposeFooter onEmojiPick={onEmojiPick} onDraw={handleOpenCanvas} />
        </>
      )}
    </form>
  );
};

const allowedAroundShortCode =
  '><\u0085\u0020\u00a0\u1680\u2000\u2001\u2002\u2003\u2004\u2005\u2006\u2007\u2008\u2009\u200a\u202f\u205f\u3000\u2028\u2029\u0009\u000a\u000b\u000c\u000d';

function useComposeHandlers({
  redirectOnSuccess,
  headless,
}: {
  redirectOnSuccess?: boolean;
  headless?: boolean;
}) {
  const text = useAppSelector((state) => state.compose.get('text') as string);

  const dispatch = useAppDispatch();

  const isModalOpen = useAppSelector((state) => state.modal.stack.size > 0);
  const isComposerChanged = useAppSelector(selectComposerIsChanged);
  useEffect(() => {
    function escapeComposer(event: KeyboardEvent) {
      const key = normalizeKey(event.key);
      if (key !== 'escape' || isModalOpen) {
        return;
      }

      if (!event.defaultPrevented) {
        dispatch(closeComposer());
      }
    }

    function closePage(event: BeforeUnloadEvent) {
      if (isComposerChanged && !headless) {
        event.preventDefault();
        return true;
      }
      return;
    }

    document.addEventListener('keydown', escapeComposer);
    window.addEventListener('beforeunload', closePage);
    return () => {
      document.removeEventListener('keydown', escapeComposer);
      window.removeEventListener('beforeunload', closePage);
    };
  }, [dispatch, headless, isComposerChanged, isModalOpen]);

  const onEmojiPick: OnEmojiPick = useCallback(
    (emoji) => {
      const position = getComposerTextarea()?.selectionStart ?? 0;
      const beforePosition = text[position - 1];
      const needsSpace =
        'custom' in emoji &&
        !!emoji.custom &&
        !!beforePosition &&
        !allowedAroundShortCode.includes(beforePosition);
      dispatch(insertEmojiCompose(position, emoji, needsSpace));
    },
    [dispatch, text],
  );

  // Submit status
  const onSubmit = useCallback(
    (event?: React.SubmitEvent) => {
      if (event?.defaultPrevented) {
        return false;
      }
      dispatch(
        submitComposer({
          redirectOnSuccess,
        }),
      );

      event?.preventDefault();
      return false;
    },
    [dispatch, redirectOnSuccess],
  );

  return {
    onSubmit,
    onEmojiPick,
  };
}
